import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import { requestNotificationPermission, onMessageListener } from '@/utils/firebase';
import { toast } from 'sonner';
import OwnerNotifications from '@/components/OwnerNotifications';
import ErrorBoundary from '@/components/ErrorBoundary';
import '@/App.css';

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
  </div>
);

// Lazy loaded pages
const PublicHomepage = lazy(() => import('@/pages/PublicHomepage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const OwnerDashboard = lazy(() => import('@/pages/owner/OwnerDashboard'));
const OwnerOrders = lazy(() => import('@/pages/owner/OwnerOrders'));
const OwnerMenu = lazy(() => import('@/pages/owner/OwnerMenu'));
const OwnerExtras = lazy(() => import('@/pages/owner/OwnerExtras'));
const OwnerStatistics = lazy(() => import('@/pages/owner/OwnerStatistics'));
const OwnerBusinessHours = lazy(() => import('@/pages/owner/OwnerBusinessHours'));
const OwnerOrderHours = lazy(() => import('@/pages/owner/OwnerOrderHours'));
const OwnerBackground = lazy(() => import('@/pages/owner/OwnerBackground'));
const OwnerProfile = lazy(() => import('@/pages/owner/OwnerProfile'));
const OwnerSmsVerification = lazy(() => import('@/pages/owner/OwnerSmsVerification'));
const OwnerPayments = lazy(() => import('@/pages/owner/OwnerPayments'));
const CustomerMenu = lazy(() => import('@/pages/customer/CustomerMenu'));
const CustomerCheckout = lazy(() => import('@/pages/customer/CustomerCheckout'));
const OrderSuccess = lazy(() => import('@/pages/customer/OrderSuccess'));
const PaymentSuccess = lazy(() => import('@/pages/customer/PaymentSuccess'));
const RestaurantLanding = lazy(() => import('@/pages/customer/RestaurantLanding'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminStatistics = lazy(() => import('@/pages/admin/AdminStatistics'));
const AdminErrors = lazy(() => import('@/pages/admin/AdminErrors'));

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.role === 'owner') {
      requestNotificationPermission();
      
      onMessageListener().then((payload) => {
        if (payload) {
          // toast.success(payload.notification?.title || 'Notifica', {
          //   description: payload.notification?.body,
          // });
          
          // Play notification sound
          const audio = new Audio('/notification.mp3');
          audio.play().catch(() => {});
          
          // Vibrate if supported
          if ('vibrate' in navigator) {
            navigator.vibrate([200, 100, 200]);
          }
        }
      });
    }
  }, [user]);

  return (
    <>
      {/* Global notification system for owner */}
      {user && user.role === 'owner' && <OwnerNotifications />}
      
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<PublicHomepage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<RegisterPage />} />
        
        {/* Owner Routes */}
        <Route
          path="/owner/dashboard"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/orders"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/menu"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerMenu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/extras"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerExtras />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/statistics"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerStatistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/business-hours"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerBusinessHours />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/order-hours"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerOrderHours />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/background"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerBackground />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/profile"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/sms-verification"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerSmsVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/payments"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerPayments />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/statistics"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminStatistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/errors"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminErrors />
            </ProtectedRoute>
          }
        />
        
        {/* Customer Routes */}
        <Route path="/restaurant/:restaurantId" element={<RestaurantLanding />} />
        <Route path="/restaurant/:restaurantId/menu" element={<CustomerMenu />} />
        <Route path="/menu/:restaurantId" element={<CustomerMenu />} />
        <Route path="/restaurant/:restaurantId/order-success" element={<OrderSuccess />} />
        <Route path="/restaurant/:restaurantId/payment-success" element={<PaymentSuccess />} />
        <Route path="/checkout" element={<CustomerCheckout />} />
        <Route path="/restaurant/:restaurantId/checkout" element={<CustomerCheckout />} />
      </Routes>
      </Suspense>
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;